import { useState, useEffect } from "react";
import { Shield, Search, Clock, User, Activity, LogIn, Plus, Check, Edit, FileText, Eye } from "lucide-react";
import axios from "axios";
import { API, useAuth } from "../contexts/AuthContext";
import { Skeleton } from "../components/ui/skeleton";

const ACTION_CONFIG = {
  login: { label: "Inicio de Sesion", icon: LogIn, color: "bg-blue-100 text-blue-700" },
  crear: { label: "Crear", icon: Plus, color: "bg-emerald-100 text-emerald-700" },
  actualizar: { label: "Actualizar", icon: Edit, color: "bg-amber-100 text-amber-700" },
  aprobar: { label: "Aprobar", icon: Check, color: "bg-green-100 text-green-700" },
  registrar: { label: "Registrar", icon: FileText, color: "bg-purple-100 text-purple-700" },
};

function getActionConfig(action) {
  return ACTION_CONFIG[action] || { label: action, icon: Activity, color: "bg-gray-100 text-gray-600" };
}

function formatTimestamp(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  return d.toLocaleString("es-PY", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function timeAgo(ts) {
  if (!ts) return "";
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Ahora mismo";
  if (mins < 60) return `Hace ${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `Hace ${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `Hace ${days}d`;
}

export default function AuditLog() {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    axios.get(`${API}/audit/logs?limit=200`, { withCredentials: true })
      .then(({ data }) => setLogs(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (user?.role !== "Superadministrador") {
    return (
      <div className="p-8 text-center">
        <Shield size={48} className="mx-auto text-gray-300 mb-3" />
        <p className="text-gray-500 font-medium">Acceso restringido</p>
        <p className="text-sm text-gray-400 mt-1">Solo el Superadministrador puede ver el registro de actividad</p>
      </div>
    );
  }

  const filtered = logs.filter(l => {
    const q = search.toLowerCase();
    return !search ||
      l.user_name?.toLowerCase().includes(q) ||
      l.user_email?.toLowerCase().includes(q) ||
      l.action?.toLowerCase().includes(q) ||
      l.resource?.toLowerCase().includes(q) ||
      l.details?.toLowerCase().includes(q);
  });

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="audit-page">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Shield size={20} className="text-amber-500" />
          <span className="text-xs font-bold uppercase tracking-[0.15em] text-amber-600">Acceso Exclusivo Superadministrador</span>
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">Registro de Actividad</h1>
        <p className="text-sm text-gray-500 mt-0.5">Control y monitoreo completo de todas las acciones en el sistema</p>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Total Actividades", value: logs.length, color: "text-[#2E6DA4]", bg: "bg-blue-50" },
          { label: "Logins Hoy", value: logs.filter(l => l.action === "login" && new Date(l.timestamp).toDateString() === new Date().toDateString()).length, color: "text-emerald-600", bg: "bg-emerald-50" },
          { label: "Creaciones", value: logs.filter(l => l.action === "crear").length, color: "text-purple-600", bg: "bg-purple-50" },
          { label: "Usuarios Unicos", value: [...new Set(logs.map(l => l.user_email))].length, color: "text-amber-600", bg: "bg-amber-50" },
        ].map(s => (
          <div key={s.label} className={`${s.bg} rounded-xl p-4 border border-gray-100`}>
            <p className="text-xs text-gray-500 mb-1">{s.label}</p>
            <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Buscar por usuario, accion, recurso..."
          className="w-full border border-gray-300 rounded-xl pl-9 pr-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4]"
          data-testid="audit-search"
        />
      </div>

      {/* Activity Timeline */}
      {loading ? (
        <div className="space-y-3">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-16" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-white rounded-xl border border-gray-200/60 text-gray-400">
          <Activity size={40} className="mb-3 text-gray-300" />
          <p className="font-medium text-gray-500">Sin actividades registradas</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm overflow-hidden">
          {/* Desktop table */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50/80 border-b border-gray-100">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Hora</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Usuario</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Rol</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Accion</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Recurso</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Detalles</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((log, i) => {
                  const cfg = getActionConfig(log.action);
                  const Icon = cfg.icon;
                  return (
                    <tr key={i} className="hover:bg-gray-50/60 transition-colors" data-testid="audit-row">
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                        <div>{formatTimestamp(log.timestamp)}</div>
                        <div className="text-gray-400">{timeAgo(log.timestamp)}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-[#2E6DA4]/10 flex items-center justify-center flex-shrink-0">
                            <span className="text-[#2E6DA4] text-xs font-bold">{log.user_name?.charAt(0) || "?"}</span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-800">{log.user_name}</p>
                            <p className="text-xs text-gray-400">{log.user_email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">{log.role}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold ${cfg.color}`}>
                          <Icon size={10} />{cfg.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600 capitalize">{log.resource}</td>
                      <td className="px-4 py-3 text-xs text-gray-500 max-w-[200px] truncate">{log.details}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="sm:hidden divide-y divide-gray-100">
            {filtered.map((log, i) => {
              const cfg = getActionConfig(log.action);
              const Icon = cfg.icon;
              return (
                <div key={i} className="p-4 space-y-2" data-testid="audit-row-mobile">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-[#2E6DA4]/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-[#2E6DA4] text-xs font-bold">{log.user_name?.charAt(0) || "?"}</span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-800">{log.user_name}</p>
                        <p className="text-xs text-gray-400">{log.role}</p>
                      </div>
                    </div>
                    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold ${cfg.color}`}>
                      <Icon size={10} />{cfg.label}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600">{log.details}</p>
                  <div className="flex items-center gap-1 text-xs text-gray-400">
                    <Clock size={10} />{timeAgo(log.timestamp)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <p className="text-xs text-gray-400 text-right">{filtered.length} actividades</p>
    </div>
  );
}
